import { GridFilterItem } from '@mui/x-data-grid-pro';

import { httpClient, httpGet, HttpMethod } from './http-client';
import { QueryOption } from '../@types';
import {
  BatchReceive,
  BatchReceiveListResponse,
  CreateShipmentData,
  IncomingShipmentType,
  NonSerializedShipmentManifestData,
  ReturnSuggestion,
  SerializedShipmentManifestData,
  Shipment,
  ShipmentBatchReceiveData,
  ShipmentByPartData,
  ShipmentListResponse,
  ShipmentManifestSuppliers,
  ShipmentManifestSummery,
  ShipmentServiceAllocationData,
  ShipmentServiceAllocationList,
  ShipmentServiceAllocations,
  ShipmentStatus,
  ShipmentType,
} from '../@types/shipment.type';
import { AddStockManifestRowItemValueType } from '../components/organisms/AddStockManifestRowItem';
import { AddStockManifestDataType } from '../pages/Shipments/AddStock/AddStockManifestFragment';
import { mapNumericEqualsOperatorForWire } from '../utils/filterModel';

// Response type for mutations that return the created/updated shipment with ID
interface ShipmentMutationResponse {
  id: number;
  [key: string]: unknown;
}

// Response type for delete operations
interface DeleteManifestResponse {
  id: number;
}

// Response type for service allocation creation. The BFF returns either the
// newly-created allocation row, or a sentinel `{ isExist: true }` when the
// allocation already exists — callers narrow with `'isExist' in response`
// before reading serviceId / shipmentId / id.
export interface ServiceAllocationCreated {
  serviceId: number;
  shipmentId: number;
  id: number;
}

export interface ServiceAllocationDuplicate {
  isExist: boolean;
}

export type ServiceAllocationResponse = ServiceAllocationCreated | ServiceAllocationDuplicate;

// Response type for individual receive operations
interface IndividualReceiveResponse {
  id: number;
}

// Manifest response for batch receive and individual receive
export interface ShipmentManifestResponse {
  serial?: SerializedShipmentManifestData[];
  nonSerial?: NonSerializedShipmentManifestData[];
}

// Serial values response
interface SerialValuesResponse {
  serials: Array<{
    serial1: string;
    serial2?: string;
    serial3?: string;
  }>;
}

export const createShipment = (shipment: CreateShipmentData): Promise<Shipment> => {
  return httpClient<Shipment>('/shipment', HttpMethod.Post, shipment);
};

export const updateShipment = (id: number, shipment: Partial<Shipment>): Promise<Shipment> => {
  return httpClient<Shipment>(`/shipment/${id}`, HttpMethod.Put, shipment);
};

/** Shipments-list filter fields backed by *numeric* columns on api-logistics
 *  (the shipment `id` plus the numeric quantity/cost fields). Their `equals`
 *  must go out as `=` — see mapNumericEqualsOperatorForWire. The quick
 *  Shipment-ID box already emits `=` directly; this also covers the advanced
 *  "Shipment ID" filter, which emits `equals`. */
export const SHIPMENT_NUMERIC_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'id',
  'shippingCost',
  'qtyTotal',
  'qtyReceived',
  'qtyPending',
]);

export const getShipments = (
  queryOptions: QueryOption,
  excludedColumns?: string,
  signal?: AbortSignal
): Promise<ShipmentListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;
  let url = `/shipment?page=${page + 1}&limit=${pageSize}&excludedColumns=${excludedColumns}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = mapNumericEqualsOperatorForWire(
      field,
      filter?.operator,
      SHIPMENT_NUMERIC_FILTER_FIELDS
    );
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  return httpGet<ShipmentListResponse>(url, signal);
};

export const getShipmentById = (id: number, signal?: AbortSignal): Promise<Shipment> => {
  return httpGet<Shipment>(`/shipment/${id}`, signal);
};

export const getAllShipmentStatus = (signal?: AbortSignal): Promise<ShipmentStatus[]> => {
  return httpGet<ShipmentStatus[]>('/shipmentStatus', signal);
};

export const getShipmentBatchReceive = (
  queryOptions: QueryOption,
  signal?: AbortSignal
): Promise<BatchReceiveListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;
  let url = `/shipment/batch/receive?page=${page + 1}&limit=${pageSize}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = filter?.operator;
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  return httpGet<BatchReceiveListResponse>(url, signal);
};

export const getReturnShipmentSuggestions = (
  sourceId: number,
  destinationId: number,
  signal?: AbortSignal
): Promise<ReturnSuggestion[]> => {
  return httpGet<ReturnSuggestion[]>(
    `/returnShipmentSuggestions/source/${sourceId}/destination/${destinationId}`,
    signal
  );
};

export const getAllShipmentTypes = (signal?: AbortSignal): Promise<ShipmentType[]> => {
  return httpGet<ShipmentType[]>('/shipmentTypes', signal);
};

export const getAllShipmentManifestsByShipmentId = (
  shipmentId: number,
  signal?: AbortSignal
): Promise<AddStockManifestDataType> => {
  return httpGet<AddStockManifestDataType>(
    `/shipmentManifestList/${shipmentId}/manifestGroup`,
    signal
  );
};

export const deleteSerializedManifests = (
  manifest: AddStockManifestRowItemValueType
): Promise<DeleteManifestResponse> => {
  return httpClient<DeleteManifestResponse>('/shipmentDeleteManifest', HttpMethod.Put, manifest);
};

export const deleteNonSerializedManifests = (
  shipmentId: number,
  manifestId: number
): Promise<void> => {
  return httpClient<void>(
    `/shipment/${shipmentId}/manifest/nonSerialized/${manifestId}`,
    HttpMethod.Delete
  );
};

export const createAddStockManifestShipment = (
  shipmentId: number,
  addStockManifest: AddStockManifestRowItemValueType[]
): Promise<ShipmentMutationResponse> => {
  return httpClient<ShipmentMutationResponse>(
    `/shipment/${shipmentId}/addstock/manifest/bulk`,
    HttpMethod.Post,
    addStockManifest
  );
};

export const createFreshStockManifestShipment = (
  addStockManifest: AddStockManifestRowItemValueType[]
): Promise<Shipment> => {
  return httpClient<Shipment>('/createFreshAddStockManifest', HttpMethod.Post, addStockManifest);
};

export const createNonSerializedManifestShipment = (
  shipmentId: number,
  nonSerializedManifest: AddStockManifestRowItemValueType[]
): Promise<ShipmentMutationResponse> => {
  return httpClient<ShipmentMutationResponse>(
    `/shipment/${shipmentId}/nonSerialized/manifest/bulk`,
    HttpMethod.Post,
    nonSerializedManifest
  );
};

export const getSerialValues = (
  serialisedManifestDataItemValue: AddStockManifestRowItemValueType
): Promise<SerialValuesResponse> => {
  return httpClient<SerialValuesResponse>(
    '/getSerialValues',
    HttpMethod.Post,
    serialisedManifestDataItemValue
  );
};

export const deleteSerializedManifestById = (id: number): Promise<void> => {
  return httpClient<void>(`/shipment/manifest/serialized/${id}`, HttpMethod.Delete);
};

export const getAllShipmentManifest = (
  partTypeId: number,
  nodeId: number,
  signal?: AbortSignal
): Promise<ShipmentManifestResponse> => {
  return httpGet<ShipmentManifestResponse>(
    `/shipments/manifest/partTypes/${partTypeId}/node/${nodeId}`,
    signal
  );
};

export const getShipmentBatchReceiveById = (
  batchId: number,
  signal?: AbortSignal
): Promise<BatchReceive> => {
  return httpGet<BatchReceive>(`/shipments/batch/${batchId}/receive`, signal);
};

export const createShipmentBatchReceive = (
  batchReceive: ShipmentBatchReceiveData
): Promise<BatchReceive> => {
  return httpClient<BatchReceive>('/shipments/batch/receive', HttpMethod.Post, batchReceive);
};

export const getAllManifestByShipment = (
  shipmentId: number,
  signal?: AbortSignal
): Promise<ShipmentManifestResponse> => {
  return httpGet<ShipmentManifestResponse>(`/shipments/${shipmentId}/manifest`, signal);
};

export const createShipmentIndividualReceive = (
  shipmentId: number,
  receive: ShipmentBatchReceiveData
): Promise<IndividualReceiveResponse> => {
  return httpClient<IndividualReceiveResponse>(
    `/shipments/${shipmentId}/individual/receive`,
    HttpMethod.Post,
    receive
  );
};

export const bulkCreateShipmentIndividualReceive = (
  shipmentId: number,
  receive: ShipmentBatchReceiveData
): Promise<IndividualReceiveResponse> => {
  return httpClient<IndividualReceiveResponse>(
    `/shipments/${shipmentId}/individual/receive/bulk`,
    HttpMethod.Post,
    receive
  );
};

export const getShipmentServiceAllocationList = (
  serviceId: number,
  signal?: AbortSignal
): Promise<ShipmentServiceAllocationList[]> => {
  return httpGet<ShipmentServiceAllocationList[]>(
    `/shipmentServiceAllocation/service/${serviceId}`,
    signal
  );
};

export const createShipmentServiceAllocation = (
  shipmentServiceAllocation: ShipmentServiceAllocationData
): Promise<ServiceAllocationResponse> => {
  return httpClient<ServiceAllocationResponse>(
    '/shipmentServiceAllocation',
    HttpMethod.Post,
    shipmentServiceAllocation
  );
};

export const deleteShipmentServiceAllocation = (id: number): Promise<void> => {
  return httpClient<void>(`/shipmentServiceAllocation/${id}`, HttpMethod.Delete);
};

export const getIncomingShipments = (
  shipmentStatus: string[],
  signal?: AbortSignal
): Promise<IncomingShipmentType[]> => {
  return httpGet<IncomingShipmentType[]>(
    `/shipmentsByShipmentStatus?shipmentStatus=${shipmentStatus}`,
    signal
  );
};

export const getShipmentServiceAllocationsByShipmentId = (
  shipmentId: number,
  signal?: AbortSignal
): Promise<ShipmentServiceAllocations[]> => {
  return httpGet<ShipmentServiceAllocations[]>(
    `/shipmentServiceAllocation/shipment/${shipmentId}`,
    signal
  );
};

export const getAddStockShipmentManifestPricing = (
  partTypeId: number,
  signal?: AbortSignal
): Promise<ShipmentManifestSuppliers[]> => {
  return httpGet<ShipmentManifestSuppliers[]>(
    `/shipmentManifest/${partTypeId}/supplierPricing`,
    signal
  );
};

export const getShipmentDetailsPartIncludes = (
  serviceId: number,
  partIds: number[]
): Promise<ShipmentManifestSummery[]> => {
  return httpClient<ShipmentManifestSummery[]>(
    `/getShipmentDetailsPartIncludesForPartAction/service/${serviceId}`,
    HttpMethod.Post,
    { partIds }
  );
};

export const getLatestShipmentByPartId = (
  partId: number,
  signal?: AbortSignal
): Promise<ShipmentByPartData> => {
  return httpGet<ShipmentByPartData>(`/latestShipmentsByPartId/${partId}`, signal);
};
